import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/news", label: "资讯" },
  { to: "/anime", label: "作品" },
  { to: "/about", label: "关于" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = keyword.trim();
    setSearchOpen(false);
    setMenuOpen(false);
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : "/search");
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-foreground-950/95 backdrop-blur-md"
            : "bg-gradient-to-b from-foreground-950/85 via-foreground-950/40 to-transparent"
        }`}
      >
        <div className="w-full px-4 md:px-8 h-16 md:h-[72px] flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group shrink-0">
            <span className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-500 flex items-center justify-center font-heading font-extrabold text-background-50 text-lg md:text-xl">
              91
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-extrabold text-background-50 text-lg md:text-xl tracking-tight">
                91动漫
              </span>
              <span className="hidden sm:block text-[10px] tracking-[0.22em] text-background-300/80 uppercase font-label mt-1">
                Anime Portal
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`relative px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 cursor-pointer ${
                  isActive(item.to)
                    ? "text-primary-400"
                    : "text-background-100/85 hover:text-background-50"
                }`}
              >
                {item.label}
                {isActive(item.to) && (
                  <span className="absolute left-1/2 -translate-x-1/2 bottom-0 w-5 h-[2px] rounded-full bg-primary-500" />
                )}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="搜索"
              className="w-9 h-9 md:w-10 md:h-10 rounded-md flex items-center justify-center text-background-100 hover:text-primary-400 hover:bg-background-50/10 transition-colors duration-200 cursor-pointer"
            >
              <i className={`${searchOpen ? "ri-close-line" : "ri-search-line"} text-lg md:text-xl`} />
            </button>
            <Link
              to="/news"
              className="hidden sm:inline-flex items-center gap-1.5 h-9 md:h-10 px-4 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-fire-line text-base" />
              今日速报
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="打开菜单"
              className="md:hidden w-9 h-9 rounded-md flex items-center justify-center text-background-50 hover:bg-background-50/10 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-menu-3-line text-xl" />
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="w-full px-4 md:px-8 pb-3 animate-fade-in">
            <form onSubmit={submitSearch} className="max-w-3xl mx-auto flex items-center gap-2">
              <div className="flex-1 flex items-center gap-2 h-11 px-3 rounded-md bg-background-50">
                <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-base" />
                </span>
                <input
                  autoFocus
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索动漫作品或资讯，例如：星海列车"
                  className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
                />
              </div>
              <button
                type="submit"
                className="h-11 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors duration-200"
              >
                搜索
              </button>
            </form>
          </div>
        )}
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <button
            type="button"
            aria-label="关闭菜单"
            onClick={() => setMenuOpen(false)}
            className="absolute inset-0 bg-foreground-950/60 cursor-pointer"
          />
          <div className="absolute right-0 top-0 h-full w-[78%] max-w-[320px] bg-foreground-950 animate-slide-in flex flex-col">
            <div className="flex items-center justify-between h-16 px-5 border-b border-background-50/10">
              <span className="font-heading font-bold text-background-50 text-base">91动漫</span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="关闭菜单"
                className="w-9 h-9 rounded-md flex items-center justify-center text-background-100 hover:bg-background-50/10 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <nav className="flex flex-col px-3 py-4 gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center justify-between px-4 py-3.5 rounded-md text-sm font-medium cursor-pointer ${
                    isActive(item.to)
                      ? "bg-primary-500/15 text-primary-400"
                      : "text-background-100 hover:bg-background-50/10"
                  }`}
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-base opacity-60" />
                </Link>
              ))}
              <Link
                to="/search"
                className="flex items-center justify-between px-4 py-3.5 rounded-md text-sm font-medium text-background-100 hover:bg-background-50/10 cursor-pointer"
              >
                搜索
                <i className="ri-search-line text-base opacity-60" />
              </Link>
            </nav>
            <div className="mt-auto px-5 pb-8">
              <Link
                to="/news"
                className="flex items-center justify-center gap-2 w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-fire-line" />
                查看今日动漫速报
              </Link>
              <p className="mt-4 text-xs text-background-400 leading-relaxed">
                本站内容为动漫资讯与作品介绍，仅供浏览与阅读。
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}